import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../api';

@Injectable()
export class GammesService {
  private _gammes$: Observable<KPI.GenericGamme[]> | null = null;
  private gammesRefresh$ = new BehaviorSubject<void>(undefined);

  private _activations$: Observable<KPI.GenericGammeActivation[]> | null = null;
  private activationsRefresh$ = new BehaviorSubject<void>(undefined);

  constructor(private api: APIService) {}

  public get genericGammes$(): Observable<KPI.GenericGamme[]> {
    if (!this._gammes$) {
      this._gammes$ = this.gammesRefresh$.pipe(
        switchMap(() => this.api.generic_gammes_list().pipe(catchError(() => of([])))),
        shareReplay(1),
      );
    }
    return this._gammes$;
  }

  public get activations$(): Observable<KPI.GenericGammeActivation[]> {
    if (!this._activations$) {
      this._activations$ = this.activationsRefresh$.pipe(
        switchMap(() => this.api.generic_gamme_activations_list().pipe(catchError(() => of([])))),
        shareReplay(1),
      );
    }
    return this._activations$;
  }

  public gammesByCluster$(clusterId: number): Observable<(KPI.GenericGamme & { cluster_is_active: boolean; activation_id: number | null })[]> {
    return combineLatest([this.genericGammes$, this.activations$]).pipe(
      map(([gammes, activations]) =>
        gammes
        .filter((gamme) => !gamme.clusters?.length || gamme.clusters.some((c) => c.id === clusterId))
        .map((gamme) => {
          const activation = activations.find(
            (a) => a.gamme === gamme.id && a.cluster === clusterId,
          );
          return {
            ...gamme,
            cluster_is_active: activation ? activation.is_active : gamme.is_active,
            activation_id: activation ? activation.id : null,
          };
        }),
      ),
    );
  }

  public refreshGammes(): void {
    this.gammesRefresh$.next();
  }

  public refreshActivations(): void {
    this.activationsRefresh$.next();
  }

  public patchGamme(id: number, payload: Partial<KPI.GenericGamme>): Observable<KPI.GenericGamme> {
    return this.api.generic_gammes_patch(id, payload).pipe(
      tap(() => this.refreshGammes()),
    );
  }

  public setClusterActivation(
    gammeId: number,
    clusterId: number,
    isActive: boolean,
    existingActivationId: number | null,
  ): Observable<KPI.GenericGammeActivation> {
    const obs$ = existingActivationId
      ? this.api.generic_gamme_activations_patch(existingActivationId, { is_active: isActive })
      : this.api.generic_gamme_activations_post({ gamme: gammeId, cluster: clusterId, is_active: isActive });

    return obs$.pipe(tap(() => this.refreshActivations()));
  }

  public setGlobalActivation(gammeId: number, isActive: boolean): Observable<KPI.GenericGamme> {
    return this.patchGamme(gammeId, { is_active: isActive });
  }
}
